/**
 * Enhanced TaskForm Component (Super Branch) - Linux-neon themed, compact layout
 */

import { useState } from 'react';
import type { Task, CreateTaskInput, UpdateTaskInput, TaskStatus, TaskPriority } from '../types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { motion } from 'framer-motion';
import { MdAdd, MdClose, MdCalendarToday, MdAlarm, MdLabel } from 'react-icons/md';
import { useTheme } from '../hooks/useTheme';

interface TaskFormProps {
  onSubmit: (task: CreateTaskInput | UpdateTaskInput) => Promise<void>;
  initialTask?: Task;
  onCancel?: () => void;
}

const TaskForm: React.FC<TaskFormProps> = ({ onSubmit, initialTask, onCancel }) => {
  const { theme } = useTheme();
  const [title, setTitle] = useState(initialTask?.title || '');
  const [description, setDescription] = useState(initialTask?.description || '');
  const [dueDate, setDueDate] = useState<Date | null>(
    initialTask?.due_date ? new Date(initialTask.due_date) : null
  );
  const [status, setStatus] = useState<TaskStatus>(initialTask?.status || 'pending');
  const [priority, setPriority] = useState<TaskPriority>(initialTask?.priority || 'medium');
  const [startTime, setStartTime] = useState<Date | null>(
    initialTask?.start_time ? new Date(initialTask.start_time) : null
  );
  const [alarmTime, setAlarmTime] = useState<Date | null>(
    initialTask?.alarm_time ? new Date(initialTask.alarm_time) : null
  );
  const [tags, setTags] = useState<string[]>(initialTask?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [reminderInterval, setReminderInterval] = useState<number | ''>(
    initialTask?.reminder_interval || ''
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      if (tags.length >= 20) {
        setError('Maximum 20 tags allowed');
        return;
      }
      setTags([...tags, trimmedTag]);
      setTagInput('');
      setError(null);
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const taskData: CreateTaskInput | UpdateTaskInput = {
        title,
        description: description || undefined,
        due_date: dueDate?.toISOString() || undefined,
        status,
        priority,
        start_time: startTime?.toISOString() || null,
        alarm_time: alarmTime?.toISOString() || null,
        tags: tags.length > 0 ? tags : undefined,
        reminder_interval: reminderInterval ? Number(reminderInterval) : null,
      };

      await onSubmit(taskData);

      if (!initialTask) {
        setTitle('');
        setDescription('');
        setDueDate(null);
        setStatus('pending');
        setPriority('medium');
        setStartTime(null);
        setAlarmTime(null);
        setTags([]);
        setReminderInterval('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      onSubmit={handleSubmit}
      className="p-4 sm:p-6 rounded-lg shadow-xl border-2 max-w-4xl mx-auto"
      style={{
        backgroundColor: theme === 'dark' ? 'rgba(30, 41, 59, 0.95)' : '#ffffff',
        borderColor: theme === 'dark' ? 'rgba(var(--accent-color-rgb), 0.3)' : '#d1d5db',
        touchAction: 'manipulation' // Prevents double-tap zoom on mobile
      }}
    >
      <h3 
        className="text-lg sm:text-xl font-bold mb-4 font-mono"
        style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}
      >
        {initialTask ? 'Edit Task' : 'Create New Task'}
      </h3>

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-red-500/20 border border-red-500 text-red-600 dark:text-red-400 px-4 py-2 rounded-lg mb-4 text-sm"
        >
          {error}
        </motion.div>
      )}

      <div className="space-y-3 sm:space-y-4">
        <div>
          <label htmlFor="title" className="block text-sm font-semibold mb-1.5" style={{ color: theme === 'dark' ? '#e5e7eb' : '#374151' }}>
            Title *
          </label>
          <input 
            id="title" 
            type="text" 
            value={title} 
            onChange={(e) => setTitle(e.target.value)} 
            required 
            className="w-full px-3 py-2 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:border-transparent text-base"
            style={{
              borderColor: theme === 'dark' ? '#4b5563' : '#d1d5db',
              backgroundColor: theme === 'dark' ? '#111827' : '#ffffff',
              color: theme === 'dark' ? '#e5e7eb' : '#111827'
            }}
            placeholder="Enter task title" 
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-semibold mb-1.5" style={{ color: theme === 'dark' ? '#e5e7eb' : '#374151' }}>
            Description
          </label>
          <textarea 
            id="description" 
            value={description} 
            onChange={(e) => setDescription(e.target.value)} 
            rows={2} 
            className="w-full px-3 py-2 border-2 rounded-lg transition-all resize-none focus:outline-none focus:ring-2 focus:border-transparent"
            style={{
              borderColor: theme === 'dark' ? '#4b5563' : '#d1d5db',
              backgroundColor: theme === 'dark' ? '#111827' : '#ffffff',
              color: theme === 'dark' ? '#e5e7eb' : '#111827'
            }}
            placeholder="Task description (optional)" 
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label htmlFor="status" className="block text-sm font-semibold mb-1.5" style={{ color: theme === 'dark' ? '#e5e7eb' : '#374151' }}>Status</label>
            <select 
              id="status" 
              value={status} 
              onChange={(e) => setStatus(e.target.value as TaskStatus)} 
              className="w-full px-3 py-2 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:border-transparent"
              style={{
                borderColor: theme === 'dark' ? '#4b5563' : '#d1d5db',
                backgroundColor: theme === 'dark' ? '#111827' : '#ffffff',
                color: theme === 'dark' ? '#e5e7eb' : '#111827'
              }}
            >
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div>
            <label htmlFor="priority" className="block text-sm font-semibold mb-1.5" style={{ color: theme === 'dark' ? '#e5e7eb' : '#374151' }}>Priority</label>
            <select 
              id="priority" 
              value={priority} 
              onChange={(e) => setPriority(e.target.value as TaskPriority)} 
              className="w-full px-3 py-2 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:border-transparent"
              style={{
                borderColor: theme === 'dark' ? '#4b5563' : '#d1d5db',
                backgroundColor: theme === 'dark' ? '#111827' : '#ffffff',
                color: theme === 'dark' ? '#e5e7eb' : '#111827'
              }}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div>
            <label htmlFor="reminderInterval" className="flex items-center gap-1.5 text-sm font-semibold mb-1.5" style={{ color: theme === 'dark' ? '#e5e7eb' : '#374151' }}>
              <MdAlarm className="dark:text-purple-400 text-purple-500" />
              Reminder (min)
            </label>
            <input 
              id="reminderInterval" 
              type="number" 
              value={reminderInterval} 
              onChange={(e) => setReminderInterval(e.target.value ? Number(e.target.value) : '')} 
              min="1" 
              max="10080" 
              className="w-full px-3 py-2 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:border-transparent"
              style={{
                borderColor: theme === 'dark' ? '#4b5563' : '#d1d5db',
                backgroundColor: theme === 'dark' ? '#111827' : '#ffffff',
                color: theme === 'dark' ? '#e5e7eb' : '#111827'
              }}
              placeholder="15" 
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label htmlFor="dueDate" className="flex items-center gap-1.5 text-sm font-semibold mb-1.5" style={{ color: theme === 'dark' ? '#e5e7eb' : '#374151' }}>
              <MdCalendarToday className="dark:text-theme text-theme" />
              Due Date
            </label>
            <DatePicker 
              selected={dueDate} 
              onChange={(date) => setDueDate(date)} 
              showTimeSelect 
              timeIntervals={15}
              showTimeInput
              dateFormat="MMM d, yyyy h:mm aa" 
              className="w-full px-3 py-2 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:border-transparent text-base"
              wrapperClassName="w-full"
              placeholderText="Select due date" 
              isClearable
              popperPlacement="bottom-start"
              withPortal={typeof window !== 'undefined' && window.innerWidth < 768}
            />
          </div>

          <div>
            <label htmlFor="startTime" className="flex items-center gap-1.5 text-sm font-semibold mb-1.5" style={{ color: theme === 'dark' ? '#e5e7eb' : '#374151' }}>
              <MdCalendarToday className="dark:text-neon-green text-green-500" />
              Start Time
            </label>
            <DatePicker 
              selected={startTime} 
              onChange={(date) => setStartTime(date)} 
              showTimeSelect 
              timeIntervals={15}
              showTimeInput
              dateFormat="MMM d, yyyy h:mm aa" 
              className="w-full px-3 py-2 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:border-transparent text-base"
              wrapperClassName="w-full"
              placeholderText="Select start" 
              isClearable
              popperPlacement="bottom-start"
              withPortal={typeof window !== 'undefined' && window.innerWidth < 768}
            />
          </div>

          <div>
            <label htmlFor="alarmTime" className="flex items-center gap-1.5 text-sm font-semibold mb-1.5" style={{ color: theme === 'dark' ? '#e5e7eb' : '#374151' }}>
              <MdAlarm className="dark:text-yellow-400 text-yellow-600" />
              Alarm Time
            </label>
            <DatePicker 
              selected={alarmTime} 
              onChange={(date) => setAlarmTime(date)} 
              showTimeSelect 
              timeIntervals={15}
              showTimeInput
              dateFormat="MMM d, yyyy h:mm aa" 
              minDate={new Date()} 
              className="w-full px-3 py-2 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:border-transparent text-base"
              wrapperClassName="w-full"
              placeholderText="Set alarm" 
              isClearable
              popperPlacement="bottom-start"
              withPortal={typeof window !== 'undefined' && window.innerWidth < 768}
            />
          </div>
        </div>

        <div>
          <label htmlFor="tagInput" className="flex items-center gap-1.5 text-sm font-semibold mb-1.5" style={{ color: theme === 'dark' ? '#e5e7eb' : '#374151' }}>
            <MdLabel className="dark:text-neon-pink text-pink-500" />
            Tags (max 20)
          </label>
          <div className="flex gap-2 mb-2 flex-col sm:flex-row">
            <input 
              id="tagInput" 
              type="text" 
              value={tagInput} 
              onChange={(e) => setTagInput(e.target.value)} 
              onKeyPress={handleTagKeyPress} 
              maxLength={50} 
              className="flex-1 px-3 py-2 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:border-transparent"
              style={{
                borderColor: theme === 'dark' ? '#4b5563' : '#d1d5db',
                backgroundColor: theme === 'dark' ? '#111827' : '#ffffff',
                color: theme === 'dark' ? '#e5e7eb' : '#111827'
              }}
              placeholder="Add a tag (press Enter)" 
            />
            <button 
              type="button" 
              onClick={addTag} 
              disabled={!tagInput.trim() || tags.length >= 20} 
              className="px-4 py-2 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 whitespace-nowrap"
              style={{
                backgroundColor: theme === 'dark' ? '#ec4899' : '#ec4899',
                color: '#ffffff'
              }}
            >
              <MdAdd size={20} />
              Add
            </button>
          </div>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, index) => (
                <span 
                  key={index} 
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm border"
                  style={{
                    backgroundColor: theme === 'dark' ? '#374151' : '#e5e7eb',
                    borderColor: theme === 'dark' ? 'rgba(236, 72, 153, 0.4)' : '#ec4899',
                    color: theme === 'dark' ? '#f3f4f6' : '#1f2937'
                  }}
                >
                  {tag}
                  <button 
                    type="button" 
                    onClick={() => removeTag(tag)} 
                    className="hover:text-red-500 transition-colors ml-1"
                  >
                    <MdClose size={16} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-3 border-t sticky bottom-0 pb-safe"
          style={{
            borderColor: theme === 'dark' ? '#374151' : '#e5e7eb',
            backgroundColor: theme === 'dark' ? 'rgba(30, 41, 59, 0.95)' : '#ffffff'
          }}
        >
          {onCancel && (
            <button 
              type="button" 
              onClick={onCancel} 
              className="px-4 sm:px-6 py-3 rounded-lg transition-all font-medium text-base"
              style={{
                backgroundColor: theme === 'dark' ? '#374151' : '#e5e7eb',
                color: theme === 'dark' ? '#e5e7eb' : '#374151'
              }}
            >
              Cancel
            </button>
          )}
          <button 
            type="submit" 
            disabled={loading || !title.trim()} 
            className="px-4 sm:px-6 py-3 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-base"
            style={{
              backgroundColor: theme === 'dark' ? '#ffffff' : '#111827',
              color: theme === 'dark' ? '#111827' : '#ffffff'
            }}
          >
            {loading ? 'Saving...' : initialTask ? 'Update Task' : 'Create Task'}
          </button>
        </div>
      </div>
    </motion.form>
  );
};

export default TaskForm;
